import Mapper from '@/models/mappers/Mapper';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import Entity from '@/models/entities/Entity';
import { getEntityApiClient } from '@/api/utils/GetClient';
import { getEntityMapper } from '@/models/mappers/utils';
import { ICustomOption, IFieldFilter, ISortFilter } from '@/models/filters/Filters';
import { QueryFilters } from '@/models/filters/QueryFilters';
import { EntityType } from '@/store/entityModules/types';
import {
  AlertColor,
  AlertMessageData,
  chooseErrorMessageData,
  dispatchAlert,
} from '@/utils/alerts';
import clone from 'lodash/clone';
import { DEFAULT_FILTER_TEXT, DEFAULT_FILTER_VALUE } from '@/utils/constants';
import { SearchQueryFilters } from '@/models/filters/SearchQueryFilters';

interface IModuleState<Item extends Entity, ItemDTO> {
  items: Item[];
  selectedItem: Item | null;
  total: number;
  loading: boolean;
  queryFilters: SearchQueryFilters;
  entityType: EntityType;
  customLoading?: boolean;
  customFilteredItems?: Item[];
  customFilters?: SearchQueryFilters;
  options?: ICustomOption[];
}

class CrudModule<Item extends Entity, ItemDTO extends Object>
  implements Module<IModuleState<Item, ItemDTO>, any> {
  namespaced: boolean = true;

  state: IModuleState<Item, ItemDTO> = {
    items: [],
    selectedItem: null,
    total: 0,
    loading: false,
    queryFilters: new QueryFilters({}),
    entityType: EntityType.ENTITY,
    options: [],
  };

  getters: GetterTree<IModuleState<Item, ItemDTO>, any> = {
    items(state): Array<Item> {
      return state.items;
    },

    total(state): number {
      return state.total;
    },

    selectedItem(state): Item | null {
      return clone(state.selectedItem);
    },

    loading(state): boolean {
      return state.loading;
    },

    customLoading(state): boolean {
      return !!state.customLoading;
    },

    /** Query filters */
    queryFilters(state): Readonly<QueryFilters> {
      return state.queryFilters;
    },
    queryRowsPerPage(state): number {
      return state.queryFilters.rowsPerPage;
    },
    queryPage(state): number {
      return state.queryFilters.page;
    },
    querySorting(state) {
      return state.queryFilters.sortFilter;
    },
    queryFilter(state) {
      return state.queryFilters.fieldFilter;
    },
  };

  actions: ActionTree<IModuleState<Item, ItemDTO>, any> = {
    //fetch both items list and total value
    async fetchItems({ state, commit, dispatch }) {
      commit('setLoading', true);

      try {
        const apiClient = getEntityApiClient(state.entityType);
        const response = await apiClient.fetchItems(state.queryFilters, state.options);
        commit('setItems', response.items);
        commit('setTotal', response.total);
        return response.items;
      } catch (err) {
        console.error(err);
        dispatchAlert(
          AlertColor.ERROR,
          {
            entityName: state.entityType,
            action: 'fetch',
          },
          dispatch,
        );
      } finally {
        commit('setLoading', false);
      }
    },

    async fetchItem({ state, commit, dispatch }, itemId: number) {
      commit('setLoading', true);

      try {
        const apiClient = getEntityApiClient(state.entityType);
        const response = await apiClient.fetchItem(<number>itemId);
        if (!response) return null;

        const mapper = <Mapper<Item, ItemDTO>>getEntityMapper(state.entityType);
        const item = mapper.mapFromDTO(response);
        await commit('setSelectedItem', item);
        return item;
      } catch (err) {
        console.error(err);
        dispatchAlert(
          AlertColor.ERROR,
          {
            entityName: state.entityType,
            action: 'fetch',
            id: itemId,
          },
          dispatch,
        );
      } finally {
        commit('setLoading', false);
      }
    },

    async updateItem({ state, commit, dispatch }, newItem: Item) {
      commit('setLoading', true);

      const alertData: AlertMessageData = {
        entityName: state.entityType,
        action: 'save',
        id: newItem.id,
      };

      try {
        const apiClient = getEntityApiClient(state.entityType);
        const mapper = <Mapper<Item, ItemDTO>>getEntityMapper(state.entityType);

        const response = await apiClient.updateItem(newItem.id, mapper.mapToDTO(newItem));
        if (!response) return null;

        const item = mapper.mapFromDTO(response);
        commit('updateItem', item);
        commit('setSelectedItem', item);

        dispatchAlert(AlertColor.SUCCESS, alertData, dispatch);
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          const errorMessage = chooseErrorMessageData(alertData, err.message);
          dispatchAlert(AlertColor.ERROR, errorMessage, dispatch);
        }
      } finally {
        commit('setLoading', false);
      }
    },

    async addItem({ state, commit, dispatch }, newItem: Item) {
      commit('setLoading', true);

      const alertData: AlertMessageData = {
        entityName: state.entityType,
        action: 'add',
        single: true,
      };

      try {
        const apiClient = getEntityApiClient(state.entityType);
        const mapper = <Mapper<Item, ItemDTO>>getEntityMapper(state.entityType);

        const response = await apiClient.addItem(mapper.mapToDTO(newItem));
        if (!response) return null;

        const item = mapper.mapFromDTO(response);
        commit('addItem', item);
        commit('setSelectedItem', item);

        dispatchAlert(AlertColor.SUCCESS, alertData, dispatch);
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          const errorMessage = chooseErrorMessageData(alertData, err.message);
          dispatchAlert(AlertColor.ERROR, errorMessage, dispatch);
        }
      } finally {
        commit('setLoading', false);
      }
    },

    async deleteItem({ state, commit, dispatch }, itemId: number) {
      commit('setLoading', true);
      const apiClient = getEntityApiClient(state.entityType);

      const alertData: AlertMessageData = {
        entityName: state.entityType,
        action: 'delete',
        id: itemId,
      };

      try {
        const response = await apiClient.deleteItem(itemId);
        if (!response) return null;

        commit('deleteItem', itemId);
        dispatchAlert(AlertColor.SUCCESS, alertData, dispatch);
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          const errorMessage = chooseErrorMessageData(alertData, err.message);
          dispatchAlert(AlertColor.ERROR, errorMessage, dispatch);
        }
      } finally {
        commit('setLoading', false);
      }
    },

    setSelectedItem({ commit }, selectedItem: Item) {
      commit('setSelectedItem', selectedItem);
    },

    /** FILTERING */
    setQueryRowsPerPage({ commit }, value: number) {
      commit('setQueryFilters', { rowsPerPage: value });
    },

    setQueryPage({ commit }, value: number) {
      commit('setQueryFilters', { page: value });
    },

    setQuerySorting({ commit }, sortFilter: ISortFilter | undefined) {
      commit('setQueryFilters', { sortFilter: sortFilter });
    },

    setQueryFiltering({ commit }, fieldFilter: IFieldFilter | undefined) {
      if (fieldFilter?.filterValue === DEFAULT_FILTER_TEXT)
        fieldFilter.filterValue = DEFAULT_FILTER_VALUE;
      commit('setQueryFilters', { fieldFilter: fieldFilter });
    },

    setOptions({ commit }, options: ICustomOption) {
      commit('setOptions', options);
    },
  };

  mutations: MutationTree<IModuleState<Item, ItemDTO>> = {
    setItems(state, items: Array<ItemDTO>) {
      try {
        const mapper = <Mapper<Item, ItemDTO>>getEntityMapper(state.entityType);
        state.items = mapper.mapFromDTOList(items);
      } catch (e) {
        console.error(e);
      }
    },

    setTotal(state, total: number) {
      state.total = total;
    },

    setSelectedItem(state, selectedItem: Item) {
      state.selectedItem = selectedItem;
    },

    updateItem(state, item: Item) {
      const index = state.items.findIndex((i: Item) => i.id === item.id);
      if (index !== -1) state.items.splice(index, 1, item);
    },

    setLoading(state, value: boolean) {
      state.loading = value;
    },

    setQueryFilters: (state, queryFilters: Partial<QueryFilters>) => {
      //need reassign to trigger the watcher
      const queryFiltersCopy = clone(state.queryFilters);
      Object.keys(queryFilters).forEach(key => {
        // @ts-ignore
        queryFiltersCopy[key] = queryFilters[key];
      });

      state.options = [];
      state.queryFilters = queryFiltersCopy;
    },

    addItem(state, item: Item) {
      state.items.push(item);
      state.total += 1;
    },

    deleteItem(state, itemId: number) {
      state.items.splice(
        state.items.findIndex(i => i.id === itemId),
        1,
      );
      state.total -= 1;
    },

    setCustomFilteredItems(state, items: Array<ItemDTO>) {
      try {
        const mapper = <Mapper<Item, ItemDTO>>getEntityMapper(state.entityType);
        state.customFilteredItems = mapper.mapFromDTOList(items);
      } catch (e) {
        console.error(e);
      }
    },

    setCustomLoading(state, value: boolean) {
      state.customLoading = value;
    },

    setOptions(state, options: ICustomOption[]) {
      state.options = options;
    },
  };
}

export { IModuleState, CrudModule };
